import { JobPosting, SchoolData } from "@nimble/candidate-api/types";

import { DirectionAndShareButtons } from "@/components/shared/jobs/direction-and-share-buttons";
import JobDetailsDescription from "@/components/shared/jobs/job-details/job-details-description";
import JobDetailsHeader from "@/components/shared/jobs/job-details/job-details-header";
import JobViewActionsContainer from "@/components/shared/jobs/job-view-actions/job-view-actions.container";
import AboutThisSchool from "@/components/shared/school/about-this-school";
import { SchoolDetailsHeaderMinimal } from "@/components/shared/school/school-details/school-details-header";
import { SchoolLinkTarget } from "@/components/shared/school/school-details/school-name-link";
import { formatAddressForMaps } from "@/lib/helpers/format-address-for-maps";
import routes from "@/lib/routes";
import { StateConfig } from "@/lib/states-config";

const LONG_DESCRIPTION_THRESHOLD = 1000;

type Props = {
  jobPosting: JobPosting;
  schoolData: SchoolData;
  stateConfig: StateConfig;
};

export default function JobDetailsContent({
  jobPosting,
  schoolData,
  stateConfig,
}: Props) {
  const isLongDescription =
    (jobPosting.job_description?.replace(/<[^>]*>/g, "").length ?? 0) >=
    LONG_DESCRIPTION_THRESHOLD;

  return (
    <div className="w-full mx-auto px-4 sm:px-6 py-6">
      <div className="flex flex-col lg:flex-row w-full gap-6 lg:gap-8">
        <div className="contents lg:flex lg:flex-col lg:gap-6 lg:flex-1 lg:pr-2">
          <div className="order-1 lg:order-none">
            <JobDetailsHeader
              jobPosting={jobPosting}
              schoolData={schoolData}
              stateConfig={stateConfig}
            />
          </div>
          {isLongDescription && (
            <div className="order-3 lg:hidden">
              <JobViewActionsContainer
                jobPosting={jobPosting}
                schoolData={schoolData}
                className="h-full py-5"
                titleId={`job-view-actions-title-mobile-${jobPosting.uuid}`}
              />
            </div>
          )}
          <div className="order-4 lg:order-none">
            <JobDetailsDescription jobPosting={jobPosting} />
          </div>
        </div>
        <div className="contents lg:block lg:w-[320px] lg:flex-shrink-0 lg:self-stretch">
          <div className="order-2 lg:order-none">
            <SchoolDetailsHeaderMinimal
              schoolData={schoolData}
              schoolLinkTarget={SchoolLinkTarget.SameTab}
              stateConfig={stateConfig}
            />
          </div>
          <div className="order-last lg:order-none">
            <JobViewActionsContainer
              jobPosting={jobPosting}
              schoolData={schoolData}
              className="h-full py-4"
              titleId={`job-view-actions-title-${jobPosting.uuid}`}
            />
          </div>
          <div className="order-3 hidden lg:order-none lg:block lg:border-t lg:border-border lg:pt-4">
            <AboutThisSchool schoolData={schoolData} />
          </div>
          <div className="order-last lg:order-none lg:pt-6">
            <DirectionAndShareButtons
              directionsUrl={
                schoolData.physical_address
                  ? formatAddressForMaps(schoolData.physical_address)
                  : undefined
              }
              entityId={jobPosting.uuid}
              shareUrl={routes.getSchoolJob({
                state: stateConfig,
                districtId: schoolData.district.nces_id,
                schoolId: schoolData.nces_id,
                jobId: jobPosting.uuid,
              })}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
